import type { Answer, Question } from '@all-for-one/shared-types'
import { useEffect, useRef, useState } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { useAddResultMutation } from 'util/rtk-api'
import { normalizeSection } from '@all-for-one/shared-types'
import { reportError } from 'util/error-reporting'
import type { StudentLiveStatus } from './use-student-live-connection'

// Answers are buffered here before every save attempt and cleared only after
// the server confirms, so a network blip can never silently destroy a
// student's work (M-BIG3 / err-1).
const PENDING_ANSWERS_KEY = 'afo:pending-answers'

type PendingBuffer = { studentId: string; lessonId: string; classId: string; answers: Answer[] }

const readPendingBuffer = (): PendingBuffer | null => {
	try {
		const raw = localStorage.getItem(PENDING_ANSWERS_KEY)
		return raw ? JSON.parse(raw) as PendingBuffer : null
	} catch { return null }
}

export type SubmitState = 'idle' | 'saving' | 'saved' | 'error'

interface UseStudentAnswersParams {
	studentId: string
	lessonId: string
	classId: string
	questions: Question[]
	currentIndex: number
	learningStage: string | null
	sessionEndedTick: number
	navigate: NavigateFunction
	setStatus: React.Dispatch<React.SetStateAction<StudentLiveStatus>>
}

export interface StudentAnswers {
	submitState: SubmitState
	getAnswer: (questionId: string) => Answer | undefined
	ensureInitializedAnswer: (q: Question) => void
	handleAnswerChange: (questionId: string, value: string | string[]) => void
	handleDone: () => void
	retrySubmit: () => void
}

/**
 * Owns the answers map and submission resilience: the localStorage pending
 * -answer buffer, 3-attempt retry with backoff, resubmitting a buffer left
 * over from a previous failed attempt, and not navigating away from a
 * session-ended event while a save is still in flight or failed.
 */
export const useStudentAnswers = ({
	studentId,
	lessonId,
	classId,
	questions,
	currentIndex,
	learningStage,
	sessionEndedTick,
	navigate,
	setStatus,
}: UseStudentAnswersParams): StudentAnswers => {
	const [ answers, setAnswers ] = useState<Record<string, Answer>>({})
	const [ addResult ] = useAddResultMutation()
	const [ submitState, setSubmitState ] = useState<SubmitState>('idle')
	const submitStateRef = useRef(submitState)
	submitStateRef.current = submitState

	// React to session end - navigate away, UNLESS a save is still in flight or
	// failed: navigating then would hide the retry UI while the answers are
	// only in the localStorage buffer.
	useEffect(() => {
		if (sessionEndedTick === 0) return
		if (submitStateRef.current === 'saving' || submitStateRef.current === 'error') {
			console.log('session ended but answers not saved yet - staying for retry')
			return
		}
		console.log('session ended, navigating away')
		navigate('/enter-code')
	}, [sessionEndedTick])

	/**
	 * Save answers with retries. The buffer is written BEFORE the first
	 * attempt and cleared only on server-confirmed success (err-1: the old
	 * code fired addResult with no await and showed "You're finished!"
	 * even when the request never made it).
	 */
	const submitAnswers = async (payload: PendingBuffer) => {
		setSubmitState('saving')
		try { localStorage.setItem(PENDING_ANSWERS_KEY, JSON.stringify(payload)) } catch {}
		const attempts = 3
		for (let attempt = 1; attempt <= attempts; attempt++) {
			try {
				await addResult({ ...payload, skipGrading: true }).unwrap()
				try { localStorage.removeItem(PENDING_ANSWERS_KEY) } catch {}
				setSubmitState('saved')
				return
			} catch (e) {
				if (attempt === attempts) {
					reportError('manual', e, { where: 'student-live submit', attempts: String(attempts) })
					setSubmitState('error')
					return
				}
				await new Promise(r => setTimeout(r, attempt * 1500))
			}
		}
	}

	// If a previous visit left unsaved answers for this student+lesson (tab
	// closed or refreshed mid-failure), resubmit them once we know who and
	// where we are.
	useEffect(() => {
		if (!studentId || !lessonId || submitStateRef.current !== 'idle') return
		const pending = readPendingBuffer()
		if (pending && pending.studentId === studentId && pending.lessonId === lessonId) {
			console.log('found unsaved answers from a previous attempt - resubmitting')
			setStatus('done')
			submitAnswers(pending)
		}
	}, [studentId, lessonId])

	const getAnswerKey = (stage: string | null, questionId: string) => `${stage || 'unknown'}:${questionId}`

	const ensureInitializedAnswer = (q: Question) => {
		const key = getAnswerKey(learningStage, (q as any).id)
		if (answers[key] !== undefined) return
		const idx = questions.findIndex(x => x.id === q.id)
		const questionText = Array.isArray((q as any).question)
			? (q as any).question.join(' _____ ')
			: String((q as any).question ?? '')
		setAnswers(prev => ({
			...prev,
			[key]: {
				index: idx >= 0 ? idx : currentIndex,
				questionId: (q as any).id,
				questionText,
				studentAnswer: '',
				section: normalizeSection(learningStage)
			}
		}))
	}

	const handleAnswerChange = (questionId: string, value: string | string[]) => {
		const studentAnswer = Array.isArray(value) ? value.join(' ') : String(value ?? '')
		const key = getAnswerKey(learningStage, questionId)
		setAnswers(prev => ({
			...prev,
			[key]: {
				...(prev[key] || { index: currentIndex, questionId, questionText: '', section: normalizeSection(learningStage) }),
				studentAnswer
			}
		}))
	}

	/**
	 * Build the final answer set synchronously: the current question's blank
	 * entry may not have flushed into `answers` state yet when Done is
	 * clicked, and a never-touched question must still submit as a blank
	 * (blanks are graded 0 server-side; a MISSING answer is ungradeable).
	 */
	const buildFinalAnswers = (): Answer[] => {
		const merged: Record<string, Answer> = { ...answers }
		questions.forEach((q, idx) => {
			const key = getAnswerKey(learningStage, (q as any).id)
			if (merged[key] === undefined) {
				const questionText = Array.isArray((q as any).question)
					? (q as any).question.join(' _____ ')
					: String((q as any).question ?? '')
				merged[key] = {
					index: idx,
					questionId: (q as any).id,
					questionText,
					studentAnswer: '',
					section: normalizeSection(learningStage)
				}
			}
		})
		return Object.values(merged)
	}

	const handleDone = () => {
		const finalAnswers = buildFinalAnswers()
		setStatus('done')
		if (!studentId || !lessonId || !classId) {
			// The old code silently SKIPPED the save here and told the student
			// they were finished. Missing identity is an error, not a skip.
			reportError('manual', new Error('student-live submit skipped: missing identity/session ids'), {
				hasStudentId: String(!!studentId), hasLessonId: String(!!lessonId), hasClassId: String(!!classId),
			})
			setSubmitState('error')
			return
		}
		submitAnswers({ studentId, lessonId, classId, answers: finalAnswers })
	}

	const retrySubmit = () => {
		const pending = readPendingBuffer()
		if (pending) {
			submitAnswers(pending)
		} else if (studentId && lessonId && classId) {
			submitAnswers({ studentId, lessonId, classId, answers: buildFinalAnswers() })
		}
	}

	const getAnswer = (questionId: string): Answer | undefined => answers[getAnswerKey(learningStage, questionId)]

	return {
		submitState,
		getAnswer,
		ensureInitializedAnswer,
		handleAnswerChange,
		handleDone,
		retrySubmit,
	}
}
